import fetch, { RequestInit, RequestInfo } from './fetch';
import { getRequestContext } from './requestContext';

const BACKOFF_FACTOR = 2.2;

export class HTTPError extends Error {
  _response: Response

  constructor (response: Response) {
    super(`Fetch error ${response.status}: ${response.statusText}`);
    // NOTE: See https://github.com/Microsoft/TypeScript/wiki/FAQ#why-doesnt-extending-built-ins-like-error-array-and-map-work
    Object.setPrototypeOf(this, HTTPError.prototype);
    this._response = response;
  }

  get response () {
    return this._response;
  }
}

export class TimeoutError extends Error {
  constructor (url: string, timeout: number) {
    super(`Request to ${url}" timed out after ${timeout}ms`);
  }
}

/*
 * fetcher is an abstraction on top of the browser fetch API. It can be used as follows:
 *
 *   import * as fetcher from '@hydra/utils/fetcher'
 *
 *   // to fetch some json
 *   await fetcher.get(url).json()
 *
 *   // with query parameters
 *   await fetcher.get(url, {
 *     searchParams: { key: 'value' }
 *   }).json()
 *
 *   // post requests (or .put, .delete)
 *   await fetcher.post(url, {
 *     body: 'post body'
 *   }).json()
 *
 *   // post json requests
 *   await fetcher.post(url, {
 *     json: { key: 'value' }
 *   }).json()
 *
 *   // if you want the raw response for http errors
 *   const response = await fetcher.get(nonExistingUrl, { throwHttpErrors: false })
 */

async function delay (ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

interface FetcherOptions extends RequestInit {
  searchParams?: string | string[][] | Record<string, string> | URLSearchParams
  credentials?: 'include' | 'omit' | 'same-origin'
  timeout?: number
  json?: any
  retry?: number
  throwHttpErrors?: boolean
  trace?: boolean
}

type HeadersArray = string[][]

interface HeadersObject {
  [key: string]: string
}

// Fetches a response with retries and HTTP error handling
async function fetchResponse (input: RequestInfo, {
  throwHttpErrors = true,
  trace = true,
  ...options
}: FetcherOptions & {headers: HeadersObject}) {
  const availableAttempts = typeof options.retry !== 'undefined'
    ? (options.retry + 1)
    : (options.method === 'GET' || options.method === 'HEAD' ? 3 : 1);
  let attempts = 0;
  let backoffInterval = 1000;

  if (trace && typeof window === 'undefined') {
    const ctx = getRequestContext();
    if (ctx) {
      options.headers['x-request-id'] = ctx.id;
    }
  }

  while (true) {
    try {
      attempts += 1;
      const response = await fetch(input, options);
      if (throwHttpErrors && !response.ok) {
        // @ts-expect-error incompatibilities between node-fetch and DOM types.
        throw new HTTPError(response);
      }
      return response;
    } catch (err) {
      if (err instanceof HTTPError) {
        throw err;
      }
      if (attempts >= availableAttempts) {
        throw err;
      }
      // back off for a moment, then retry
      await delay(backoffInterval);
      backoffInterval *= BACKOFF_FACTOR;
    }
  }
}

function createFetchMethod (method: string) {
  return function doFetch (input: RequestInfo | URL, {
    searchParams,
    json,
    // will add a default timeout of 10000 later
    timeout,
    ...options
  }: FetcherOptions = {}) {
    let fetchInput: RequestInfo;
    if (typeof input === 'string' || input instanceof URL) {
      const url = new URL(String(input), typeof window === 'undefined' ? undefined : window.location.href);

      if (searchParams) {
        for (const key of url.searchParams.keys()) {
          url.searchParams.delete(key);
        }
        const newParams = new URLSearchParams(searchParams);
        for (const [key, value] of newParams.entries()) {
          url.searchParams.append(key, value);
        }
      }

      fetchInput = url.toString();
    } else {
      fetchInput = input;
    }
    options.method = method;

    const headers = (options.headers
      ? (
          typeof (options.headers as HeadersArray)[Symbol.iterator] === 'function'
            ? Array.from(options.headers as HeadersArray).reduce((result, [key, value]) => {
                result[key] = key in result ? result[key] + ', ' + value : value;
                return result;
              }, {} as HeadersObject)
            : options.headers
        )
      : {}) as HeadersObject;
    delete options.headers;

    if (json) {
      if (options.body) {
        throw new Error('can\'t specify json and body at the same time');
      }
      headers['content-type'] = 'application/json';
      options.body = JSON.stringify(json);
    } else if (options.body instanceof URLSearchParams) {
      headers['content-type'] = 'application/x-www-form-urlencoded';
      options.body = String(options.body);
    }

    const promises = [
      fetchResponse(fetchInput, {
        // default by spec, but not in all browsers yet:
        credentials: 'same-origin',
        ...options,
        headers
      })
    ];

    if (timeout) {
      // naive timeout logic, will improve with abort logic in the future
      promises.push(new Promise((resolve, reject) => {
        setTimeout(() => reject(new TimeoutError(String(fetchInput), timeout)), timeout);
      }));
    }

    const responsePromise = Promise.race(promises);

    return Object.assign(responsePromise, {
      // shorthand for .json()
      json: async () => (await responsePromise).json(),
      // shorthand for .text()
      text: async () => (await responsePromise).text(),
      // shorthand for .blob()
      blob: async () => (await responsePromise).blob(),
      // shorthand for .buffer(), node.js only!!!
      buffer: async () => (await responsePromise).buffer()
    });
  };
}

export * from './fetch';
export const get = /* #__PURE__ */createFetchMethod('GET');
export const post = /* #__PURE__ */createFetchMethod('POST');
export const put = /* #__PURE__ */createFetchMethod('PUT');
export const patch = /* #__PURE__ */createFetchMethod('PATCH');
export const head = /* #__PURE__ */createFetchMethod('HEAD');
export const del = /* #__PURE__ */createFetchMethod('DELETE');
